import React, { useState } from 'react';
import { Users, MousePointerClick, Eye, Download, Percent, Globe } from 'lucide-react';
import {
  useWebOverview,
  useWebAcquisition,
  useWebCityEngagement,
  useWebTimeseries,
  usePanelTopLots,
} from '../../lib/api';
import { DateRange, WebTimeseriesMetric, CitySlug } from '../../lib/panelTypes';
import StatCard from './StatCard';
import TimeSeriesChart from './TimeSeriesChart';
import SeriesModeToggle from './SeriesModeToggle';

const num = (n: number) => (n || 0).toLocaleString();
const pct = (n: number) => `${(n || 0).toFixed(1)}%`;

const CITY_LABELS: Record<string, string> = {
  aalborg: 'Aalborg',
  aarhus: 'Aarhus',
  koebenhavn: 'København',
};

// City filter for the Top lots table. '' = all cities (single global ranking).
const CITY_TABS: { key: '' | CitySlug; label: string }[] = [
  { key: '', label: 'All cities' },
  { key: 'aalborg', label: 'Aalborg' },
  { key: 'aarhus', label: 'Aarhus' },
  { key: 'koebenhavn', label: 'København' },
];

const METRICS: { key: WebTimeseriesMetric; label: string; color: string }[] = [
  { key: 'visitors', label: 'Visitors', color: '#4f46e5' },
  { key: 'page_views', label: 'Page views', color: '#0891b2' },
  { key: 'lot_views', label: 'Lot views', color: '#16a34a' },
  { key: 'download_clicks', label: 'Download clicks', color: '#f59e0b' },
];

const WebsiteTab: React.FC<{ range: DateRange }> = ({ range }) => {
  const [metric, setMetric] = useState<WebTimeseriesMetric>('visitors');
  const [cumulative, setCumulative] = useState(false);
  const [lotsCity, setLotsCity] = useState<'' | CitySlug>('');
  const { data: overview, isLoading } = useWebOverview(range);
  const { data: acq } = useWebAcquisition(range);
  const { data: cityEng } = useWebCityEngagement(range);
  const { data: ts } = useWebTimeseries(metric, range, cumulative);
  const { data: topLots } = usePanelTopLots(range, 15, 'website', lotsCity || undefined);

  const k = overview?.kpis;
  const activeMetric = METRICS.find((m) => m.key === metric)!;
  const hasData = (k?.visitors.value ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Acquisition & conversion KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Visitors" value={num(k?.visitors.value ?? 0)} changePct={k?.visitors.changePct} icon={Users} />
        <StatCard label="Engaged visitors" value={num(k?.engagedVisitors.value ?? 0)} changePct={k?.engagedVisitors.changePct} icon={MousePointerClick} hint="used the map / searched" />
        <StatCard label="Page views" value={num(k?.pageViews.value ?? 0)} changePct={k?.pageViews.changePct} icon={Eye} />
        <StatCard label="Download clicks" value={num(k?.downloadClicks.value ?? 0)} changePct={k?.downloadClicks.changePct} icon={Download} />
        <StatCard label="Visit → download" value={pct(k?.visitToDownloadRate.value ?? 0)} changePct={k?.visitToDownloadRate.changePct} icon={Percent} hint="visitors who clicked a store" />
      </div>

      {!hasData && (
        <div className="bg-white border rounded-lg p-8 text-center">
          <Globe className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600 font-medium">No website analytics in this range yet</p>
          <p className="text-sm text-gray-400 mt-1">
            The website funnel fills once the updated freespot.dk (first-party web_events) is live and visitors accept analytics cookies.
          </p>
        </div>
      )}

      {/* Trend */}
      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-gray-900">Trend over time</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-gray-200 p-0.5">
              {METRICS.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMetric(m.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded ${
                    metric === m.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <SeriesModeToggle cumulative={cumulative} onChange={setCumulative} />
          </div>
        </div>
        {cumulative && (
          <p className="text-xs text-gray-400 -mt-2 mb-3">
            {metric === 'visitors'
              ? `Unique visitors seen since ${range.startDate} — each visitor counted once, on their first day.`
              : `Running total since ${range.startDate}.`}
          </p>
        )}
        <TimeSeriesChart data={ts?.series ?? []} color={activeMetric.color} cumulative={cumulative} />
      </div>

      {/* Acquisition & conversion funnel */}
      <div className="bg-white border rounded-lg p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Acquisition &amp; conversion funnel</h3>
        {(acq?.steps?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-400">No visits in this range.</p>
        ) : (
          <div className="space-y-3">
            {acq!.steps.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{s.label}</span>
                  <span className="text-gray-500">
                    {num(s.count)} · {s.pctOfStart.toFixed(0)}%
                    {s.key !== 'visited' && <span className="text-gray-400"> ({s.pctOfPrev.toFixed(0)}% of prev)</span>}
                  </span>
                </div>
                <div className="h-6 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded" style={{ width: `${Math.max(2, s.pctOfStart)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Channels + referrers */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Channels</h3>
          {(acq?.channels?.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-400">No data.</p>
          ) : (
            <div className="space-y-2">
              {acq!.channels.map((ch) => (
                <div key={ch.channel} className="flex justify-between text-sm">
                  <span className="text-gray-700 capitalize">{ch.channel}</span>
                  <span className="text-gray-500">{num(ch.visitors)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Top referrers</h3>
          {(acq?.referrers?.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-400">No external referrers.</p>
          ) : (
            <div className="space-y-2">
              {acq!.referrers.map((r) => (
                <div key={r.referrer} className="flex justify-between text-sm">
                  <span className="text-gray-700 truncate mr-2">{r.referrer}</span>
                  <span className="text-gray-500 whitespace-nowrap">{num(r.visitors)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* UTM campaigns */}
      {(acq?.campaigns?.length ?? 0) > 0 && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-1">UTM campaigns</h3>
          <p className="text-xs text-gray-400 mb-4">
            QR/print campaigns are cookieless — track them via Scans (reached /download) and Store clicks. For embeds the funnel reads CTA clicks &rarr; Scans &rarr; Store clicks: the first two differ by clicks that never landed, e.g. a popup blocked in an in-app browser. Logo clicks are the other exit from the map, to the front page. Visitors counts only cookie-consented web visits.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-medium">Campaign</th>
                <th className="py-2 font-medium">Source / Medium</th>
                <th className="py-2 font-medium text-right">CTA clicks</th>
                <th className="py-2 font-medium text-right">Scans</th>
                <th className="py-2 font-medium text-right">Store clicks</th>
                <th className="py-2 font-medium text-right">Logo clicks</th>
                <th className="py-2 font-medium text-right">Visitors</th>
              </tr>
            </thead>
            <tbody>
              {acq!.campaigns.map((c, i) => (
                <tr key={`${c.campaign}-${i}`} className="border-b last:border-0">
                  <td className="py-2 font-medium text-gray-900">{c.campaign}</td>
                  <td className="py-2 text-gray-600">{[c.source, c.medium].filter(Boolean).join(' / ') || '—'}</td>
                  <td className="py-2 text-right">{num(c.ctaClicks)}</td>
                  <td className="py-2 text-right">{num(c.landingViews)}</td>
                  <td className="py-2 text-right">{num(c.downloadClicks)}</td>
                  <td className="py-2 text-right">{num(c.logoClicks)}</td>
                  <td className="py-2 text-right">{num(c.visitors)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-city parkeringskort engagement */}
      <div className="bg-white border rounded-lg p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Parkeringskort engagement per city</h3>
        {(cityEng?.cities?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-400">No map engagement in this range.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-medium">City</th>
                <th className="py-2 font-medium text-right">Visitors</th>
                <th className="py-2 font-medium text-right">Map views</th>
                <th className="py-2 font-medium text-right">Lot views</th>
                <th className="py-2 font-medium text-right">App intent</th>
              </tr>
            </thead>
            <tbody>
              {cityEng!.cities.map((c) => (
                <tr key={c.city} className="border-b last:border-0">
                  <td className="py-2 font-medium text-gray-900">{CITY_LABELS[c.city] || c.city}</td>
                  <td className="py-2 text-right">{num(c.visitors)}</td>
                  <td className="py-2 text-right">{num(c.mapViews)}</td>
                  <td className="py-2 text-right">{num(c.lotViews)}</td>
                  <td className="py-2 text-right">{num(c.appIntent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top lots viewed on the website */}
      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-gray-900">Top lots (website)</h3>
          <div className="inline-flex rounded-md border border-gray-200 p-0.5">
            {CITY_TABS.map((c) => (
              <button
                key={c.key || 'all'}
                onClick={() => setLotsCity(c.key)}
                className={`px-2.5 py-1 text-xs font-medium rounded ${
                  lotsCity === c.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        {(topLots?.lots?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-400">No lot views on the website in this range.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-medium">#</th>
                <th className="py-2 font-medium">Lot ID</th>
                <th className="py-2 font-medium">City</th>
                <th className="py-2 font-medium">Address</th>
                <th className="py-2 font-medium text-right">Web views</th>
              </tr>
            </thead>
            <tbody>
              {topLots!.lots.map((lot, i) => (
                <tr key={lot.spotId} className="border-b last:border-0">
                  <td className="py-2 text-gray-400">{i + 1}</td>
                  <td className="py-2 font-medium text-gray-900">{lot.spotId}</td>
                  <td className="py-2 text-gray-600">{lot.city ? (CITY_LABELS[lot.city] || lot.city) : '—'}</td>
                  <td className="py-2 text-gray-600">{lot.address || '—'}</td>
                  <td className="py-2 text-right">{num(lot.webViews)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
    </div>
  );
};

export default WebsiteTab;
